import { defineStore } from 'pinia';
import api from '@/api/api';
import {type ZEvent, type ZQueryResultEvents} from '@schemas/event.schemas';
import type {Id, QueryResult} from "@typesDef/general.types";
import {setIdsUnique} from "@/stores/helpers/storeHelper";
import type {ZHydratedEvent} from "@schemas/hydrated.schemas";

export const useEventsStore = defineStore('events', {
    state: () => ({
        eventsById: {} as Record<Id, ZEvent>,
        myEventIds: [] as Id[],
        publicEventIds: [] as Id[],
        currentEventId: null as Id | null,
        // events per network, used in the context of the network details page
        // use QueryResult<Id> results and count
        networkEventsMeta: {} as Record<Id, QueryResult<Id>>
    }),
    getters: {
        currentEvent(state): ZEvent | null {
            return state.currentEventId ? state.eventsById[state.currentEventId] ?? null : null;
        },
        myEvents(state): ZEvent[] {
            return state.myEventIds.map(id => state.eventsById[id]).filter(Boolean) as ZEvent[];
        },
        // FIXME rename to publicEvents
        publicEvents(state): ZEvent[] {
            return state.publicEventIds.map(id => state.eventsById[id]).filter(Boolean) as ZEvent[];
        },

        // need for provider when for example request an announcer to select a specific event
        myAvailableEvents(state): ZEvent[] {
            if (state.myEventIds?.length === 0) {
                return [];
            }
            // we filter out drafts and events with closed applications, since they are not available for providers to request
            return this
                .myEvents
                .filter(
                    (event) =>
                        // check if not deleted
                        event.isDraft !== true
                        // we also do not consider application.status as well
                        // && event.application?.status === 'open'
                        // we do not consider deadline if a provider wants to request he should be able to
                        // && (event.isOnboardingEvent || new Date(event?.application?.deadline) > new Date())
                )
                .sort((a, b) => a.title.localeCompare(b.title));
        },
        networkEventsFor: (state) => (networkId: Id): ZQueryResultEvents => {
            // we get the meta for the network, which contains the count and the list of event IDs for that network
            const meta = state.networkEventsMeta[networkId];

            if (!meta) {
                return { count: 0, results: [] };
            }

            return {
                count: meta.count,
                results: meta.results
                    .map(id => state.eventsById[id])
                    .filter(Boolean) as ZEvent[],
            };
        },
    },
    actions: {
        clearMyData() {
            this.eventsById = {};
            this.myEventIds = [];
            this.publicEventIds = [];
            this.currentEventId = null;
            this.networkEventsMeta = {};
        },
        // FIXME move them to general library since they are used in multiple stores
        upsertMany(docs: ZEvent[]) {
            for (const e of docs) {
                this.eventsById[e._id] = this.mergeEvent(this.eventsById[e._id], e);
            }
        },
        /**
         * Merges an old event with a new event. The new event takes precedence over the old event,
         * but if the new event does not have certain details (like profileDetails or networkDetails) and is not hydrated,
         * it retains those from the old event to prevent "downgrading" when list payloads don't include these details.
         * FIXME this is workaround
         */
        mergeEvent (oldE: ZEvent | ZHydratedEvent | undefined, newE: ZEvent | ZHydratedEvent): ZEvent | ZHydratedEvent {
            log.debug(`mergeEvent oldE: ${oldE?._id} profileDetails: ${oldE?.profileDetails} networkDetails: ${oldE?.networkDetails}, newE: ${newE._id} profileDetails: ${newE.profileDetails} networkDetails: ${newE.networkDetails}`);
            if (!oldE) return newE;

            return {
                ...oldE,
                ...newE,

                // Prevent “downgrade” when list payload doesn’t include these:
                profileDetails: newE.profileDetails ?? oldE.profileDetails,
                networkDetails: newE.networkDetails ?? oldE.networkDetails,
            };
        },
        // FIXME this is a workaround (my liste events fetch on profileDetails and without merge above and this
        //  it would load the details everytime.
        async fetchEvent(eventId: Id): Promise<ZEvent> {
            log.debug(`fetchEvent(${eventId})`);
            const cached = this.eventsById[eventId];
            // check if full hydrated event is in the cache, if not fetch from backend, if yes use cache and set currentEventId
            log.debug(`cached event: ${cached?._id}, profileDetails: ${cached?.profileDetails}, networkDetails: ${cached?.networkDetails}`);
            if(cached && cached.profileDetails && cached.networkDetails) {
                this.currentEventId = eventId;
                return cached;
            }
            const doc = await api.getDocument<ZEvent>('events', eventId);
            this.eventsById[doc._id] = doc;
            this.currentEventId = doc._id;
            return doc;
        },
        async fetchEvents(query: Record<string, any> = {}): Promise<ZEvent[]> {
            const docs = await api.getDocuments<ZEvent>('events', query);
            this.upsertMany(docs);
            setIdsUnique(this.publicEventIds, docs.map(d => d._id));
            // add to networkEventsMeta

            return docs;
        },
        async fetchMyEvents(query: Record<string, any> = {}): Promise<ZEvent[]> {
            // TODO add caching mechanism i.e. check if the amount of loaded my events is the same as the amount of events in the backend, if not fetch, otherwise use cache
            const docs = await api.getDocuments<ZEvent>('events/my', query);
            this.upsertMany(docs);
            setIdsUnique(this.myEventIds, docs.map(d => d._id));
            return docs;
        },
        /**
         * Fetch events for a specific network.
         * @param networkId
         * @param query
         * @returns {Promise<ZQueryResultEvents>}
         */
        async fetchEventsByNetworkId(networkId: Id, query: Record<string, any> = {}): Promise<ZQueryResultEvents> {
            log.debug(`fetchEventsByNetworkId(${networkId}) query=${JSON.stringify(query)})`);
            if(!networkId) {
                throw new Error('Network ID is required to fetch events by network');
            }

            // if cached
            const cachedMeta = this.networkEventsMeta[networkId];
            if(cachedMeta) {
                log.debug(`Using cached events for network ${networkId}, count: ${cachedMeta.count}`);
                return {
                    count: cachedMeta.count,
                    results: cachedMeta.results
                        .map(id => this.eventsById[id])
                        .filter(Boolean) as ZEvent[],
                };
            }
            const metaDocs = await api.getDocumentsWithCount<ZEvent>(
                `events/network/${networkId}`,
                query
            );

            this.upsertMany(metaDocs.results);

            this.networkEventsMeta[networkId] = {
                results: [...new Set(metaDocs.results.map(e => e._id))],
                count: metaDocs.count
            };

            return metaDocs;
        },

        /**
         * Create a new event. The created event is added to the "eventsById" map and its ID is added to both "myEventIds" and
         * "publicEventIds" lists since it's owned by the user and also public by default. The "currentEventId" is set to the created event's ID.
         * @param event The event to be created
         * @return The created event
         */
        async addEvent(event: ZEvent): Promise<ZEvent> {
            const created = await api.postDocument<ZEvent>('events', event);
            this.eventsById [created._id] = created;
            // add event to both lists (my and public) since it's mine and also public at least for me (draft: false) by default
            if(!this.myEventIds.includes(created._id)) {
                this.myEventIds.unshift(created._id);
            }
            if(!this.publicEventIds.includes(created._id)) {
                this.publicEventIds.unshift(created._id);
            }
            // network
            if(created.networkIds?.length) {
                const networkIds = created?.networkIds;
                // iterate over all networkIds of the event and add the event to the corresponding network meta
                for(const networkId of networkIds) {
                    // get the meta for the network, if it doesn't exist create a new one
                    const meta = this.networkEventsMeta[networkId] ?? { results: [], count: 0 };
                    // if the event is not already in the meta, add it and increase the count
                    if(!meta.results.includes(created._id)) {
                        meta.results.unshift(created._id);
                        meta.count += 1;
                        this.networkEventsMeta[networkId] = meta;
                    }
                }
            }
            this.currentEventId = created._id;
            return created;
        },
        /**
         * Update an existing event. The updated event is added to the "eventsById" map,
         * replacing the previous version of the event. The "myEventIds" and "publicEventIds" lists
         * are not modified since the event's ID does not change and we assume that
         * the ownership and visibility of the event do not change when updating.
         */
        async updateEvent(event: ZEvent): Promise<ZEvent> {
            const updated = await api.putDocument<ZEvent>('events', event);
            // FIXME update could mean that a new network was added or removed from the event, so we need to update the networkEventsMeta accordingly
            this.eventsById [updated._id] = updated;
            return updated;
        },
        async deleteEvent(eventId: Id): Promise<void> {
            await api.deleteDocument<ZEvent>('events', eventId);
            delete this.eventsById[eventId];
            this.myEventIds = this.myEventIds.filter(id => id !== eventId);
            this.publicEventIds = this.publicEventIds.filter(id => id !== eventId);
            // also remove from networkEventsMeta
            this.networkEventsMeta = Object.fromEntries(
                Object.entries(this.networkEventsMeta).map(([networkId, meta]) => [
                    networkId,
                    {
                        results: meta.results.filter(id => id !== eventId),
                        count: meta.count - (meta.results.includes(eventId) ? 1 : 0),
                    },
                ])
            );
            // if the deleted event was the current one, reset currentEventId
            if(this.currentEventId === eventId) {
                this.currentEventId = null;
            }
        }
    }
});
